import React, { useState, FC, useEffect } from 'react';
import { buildLocStoreKeys } from 'store/types';

interface IContextState {
  is2FaComplete: boolean | null;
  is2FaEnabled: boolean | null;
  setIs2FaComplete: (value: boolean) => void;
  setIs2FaEnabled: (value: boolean) => void;
  set2FaKeys: ({ is2FaEnabled, is2FaComplete }: { is2FaEnabled: boolean; is2FaComplete: boolean }) => void;
}

export const TwoFactorContext = React.createContext<IContextState>({
  setIs2FaComplete: () => {},
  setIs2FaEnabled: () => {},
  set2FaKeys: () => {},
  is2FaComplete: null,
  is2FaEnabled: null,
});

interface IProps {
  children: React.ReactNode;
}

const LocStoreKeys = buildLocStoreKeys();

export const TwoFactorProvider: FC<IProps> = ({ children }: IProps) => {
  const [is2FaEnabled, setIs2FaEnabled] = useState<boolean | null>(
    window.localStorage.getItem(LocStoreKeys.IS_2FA_ENABLED) === 'true' || null
  );

  const [is2FaComplete, setIs2FaComplete] = useState<boolean | null>(
    window.localStorage.getItem(LocStoreKeys.IS_2FA_COMPLETE) === 'true' || null
  );

  /**
   * Subscribe to local storage updates to handle the case
   * if app is open in more than one tab
   * and 2FA settings has been updated in any of the tabs
   */
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === LocStoreKeys.IS_2FA_ENABLED) {
        setIs2FaEnabled(e.newValue === 'true');
      }
      if (e.storageArea === localStorage && e.key === LocStoreKeys.IS_2FA_COMPLETE) {
        setIs2FaComplete(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
    };
  });

  const setIs2FaEnabledStateAndStore = (value: boolean) => {
    setIs2FaEnabled(value);
    if (typeof value === 'boolean') return window.localStorage.setItem(LocStoreKeys.IS_2FA_ENABLED, value.toString());

    return window.localStorage.removeItem(LocStoreKeys.IS_2FA_ENABLED);
  };

  const setIs2FaCompleteStateAndStore = (value: boolean) => {
    setIs2FaComplete(value);
    if (typeof value === 'boolean') return window.localStorage.setItem(LocStoreKeys.IS_2FA_COMPLETE, value.toString());

    return window.localStorage.removeItem(LocStoreKeys.IS_2FA_COMPLETE);
  };

  const set2FaKeys = ({
    is2FaEnabled: is2FaEnabl,
    is2FaComplete: is2FaCompl,
  }: {
    is2FaEnabled: boolean;
    is2FaComplete: boolean;
  }) => {
    setIs2FaEnabledStateAndStore(is2FaEnabl);
    setIs2FaCompleteStateAndStore(is2FaCompl);
  };

  return (
    <TwoFactorContext.Provider
      value={{
        is2FaEnabled,
        is2FaComplete,
        setIs2FaEnabled: setIs2FaEnabledStateAndStore,
        setIs2FaComplete: setIs2FaCompleteStateAndStore,
        set2FaKeys,
      }}
    >
      {children}
    </TwoFactorContext.Provider>
  );
};

export function useTwoFactorContext(): IContextState {
  return React.useContext(TwoFactorContext);
}
